/**
 * Receipt state machine — the single source of truth.
 *
 * Every API route and every worker transitions a receipt ONLY through
 * `transition()` below, which enforces: legal predecessor state, actor
 * authority, the named guard, and optimistic concurrency on row_version.
 * A bare UPDATE of receipt.state anywhere else is a bug.
 *
 * Effects listed on a transition run inside the same DB transaction unless
 * marked `(async)`, in which case they are enqueued after commit.
 */

export type ReceiptState =
  | 'uploaded'                  // row exists, photo stored, extraction queued
  | 'extracting'                // extraction worker has claimed it
  | 'unconfirmed'               // extracted; waiting for the employee's batched check
  | 'needs-retake'              // extraction failed or low confidence; employee must re-photograph
  | 'routed'                    // confirmed; recovery dispatch decided, not yet launched
  | 'awaiting'                  // a recovery attempt is in flight or a reply is expected
  | 'queued'                    // a human owns the next step
  | 'window-expired-escalated'  // merchant window passed; direct-request / denuncia path
  | 'validated'                 // a factura is linked and passed validation
  | 'filed'                     // documents in SharePoint; reporting-complete
  | 'refacturacion'             // factura exists but is wrong; cancel-and-reissue in progress
  | 'foreign-pending-review'    // foreign receipt confirmed; awaiting completeness check
  | 'foreign-filed'             // foreign comprobante complete and filed
  | 'voided'                    // operator removed it; excluded from totals and exports
  | 'non-deductible';           // reporting-terminal with one exit (see LATE_FACTURA)

/** States the sweeps and exports treat as done. */
export const TERMINAL: ReadonlySet<ReceiptState> = new Set([
  'filed', 'foreign-filed', 'voided', 'non-deductible',
]);

/** States in which a recovery attempt may be in flight. The annual sweep must skip these when an in_flight row exists. */
export const RECOVERABLE: ReadonlySet<ReceiptState> = new Set([
  'routed', 'awaiting', 'queued', 'window-expired-escalated', 'refacturacion',
]);

export type Actor =
  | 'employee'      // via the authenticated API, scoped to own receipts
  | 'operator'      // factura-admin role
  | 'extract'       // extract-receipt worker
  | 'route'         // route-recovery worker
  | 'recovery'      // email-request / portal dispatcher / vendor adapter
  | 'ingest'        // ingest-cfdi (upload + inbox + portal result + vendor result)
  | 'orchestrator'  // deadline-orchestrator
  | 'escalation'    // escalate-expired worker
  | 'sweep'         // annual-sweep
  | 'close'         // monthly-close
  | 'sat';          // sat-status-check

export type ReasonCode =
  | 'never-facturable' | 'cash-27iii' | 'no-channel-printed' | 'channel-not-captured'
  | 'own-rfc' | 'already-global' | 'window-expired' | 'unrecovered-at-return'
  | 'captcha' | 'captcha-token-rejected' | 'login-required' | 'bot-check' | 'portal-down'
  | 'field-mismatch' | 'datos-receptor-rechazados' | 'agent-low-confidence' | 'bad-address'
  | 'ambiguous-match' | 'extraction-failed' | 'foreign-incomplete' | 'cancelled-by-emisor'
  | 'efos-definitivo' | 'operator-void'
  | 'step-cap' | 'token-cap' | 'timeout'            // portal run caps (PortalFailureReason)
  | 'cfdi-unverified'                               // the SAT does not know the CFDI (consulta → unknown): never files (security fixes, 6 Sep 2026)
  | 'new-portal'                                    // the ticket's portal domain is not in portal_domain: the operator confirms it once
  | 'unsolicited-mail'                              // a mail tied to no receipt and no sender we wrote to: no Claude draft, no one-click reply
  | 'needs-input'                                   // the portal asks a value only the employee knows (which branch): an employee item with the question (28 Oct 2026)
  | 'partial-not-supported'                         // a shared bill on a portal that can only invoice the whole ticket (migration 006): the employee chooses how to go on
  | 'partial-whole-cfdi'                            // a CFDI for the whole ticket arrived for a shared bill: never linked, the operator decides (migration 006)
  | 'self-request'                                  // the employee asked the merchant for their share's factura themselves: a CFDI by mail is expected
  // portal accounts (migration 007): the receipt waits on the company's account on its portal, or the account failed
  | 'account-pending'                               // another receipt is registering the (host, company) account: this one goes on when it is ready
  | 'awaiting-confirmation'                         // registered; the portal's confirmation mail is expected
  | 'phone-required' | 'registration-failed' | 'login-failed' | 'confirmation-unverified';

export type Event =
  | 'upload.received'
  | 'extract.claimed'
  | 'extract.completed'
  | 'extract.low_confidence'
  | 'extract.failed'
  | 'retake.received'
  | 'voucher.received'           // a shared bill's voucher (re)taken, or added from Por revisar when the question was answered there
  | 'confirm.submitted'
  | 'route.dispatched'
  | 'route.no_channel'
  | 'route.partial_unsupported'  // a shared bill whose portal is known to invoice only whole tickets: no run
  | 'attempt.succeeded'          // recovery produced a CFDI
  | 'operator.reply_sent'          // the operator answered a merchant's human mail from the console (screen 11); ingest will validate
  | 'attempt.failed_retryable'
  | 'attempt.failed_terminal'
  | 'attempt.failed_after_window'
  | 'attempt.cap_exhausted'
  | 'attempt.breaker_open'
  | 'factura.validated'          // ingest linked a factura and validation passed
  | 'factura.wrong_data'         // ingest linked a factura but receptor/total check failed
  | 'factura.ambiguous'          // matcher found >1 candidate
  | 'factura.held'               // a whole-ticket CFDI for a shared bill (or one above the voucher): never linked
  | 'filing.completed'
  | 'window.expired'
  | 'escalation.resolved_manual'
  | 'queue.retry'
  | 'queue.manual_match'
  | 'queue.give_up'
  | 'queue.channel_supplied'
  | 'queue.input_supplied'
  | 'queue.self_request'         // partial-not-supported: the employee asks the merchant for their share themselves
  | 'queue.employee_give_up'     // partial-not-supported: the employee marks it no facturable (a voucher backs the share)
  | 'account.ready'              // the (host, company) portal account it waited on is usable (confirmation link, active, entered by hand)
  | 'foreign.complete'
  | 'foreign.incomplete'
  | 'refacturacion.completed'
  | 'refacturacion.abandoned'
  | 'sweep.unrecovered'
  | 'late_factura'
  | 'factura.cancelled'
  | 'operator.void';

export interface Transition {
  from: ReceiptState | ReceiptState[];
  event: Event;
  to: ReceiptState;
  actor: Actor | Actor[];
  /** Precondition beyond state + actor; enforced in code, named here so it is not forgotten. */
  guard?: string;
  /** Reason code written to receipt.reason_code when the transition fires. */
  reason?: ReasonCode | 'from-attempt' | 'from-classification' | null;   // null = clear the stale code
  /** Side effects in order. Same transaction unless marked (async). */
  effects: string[];
}

export const TRANSITIONS: readonly Transition[] = [

  // ── Intake ────────────────────────────────────────────────────────────────
  {
    from: [] as ReceiptState[], event: 'upload.received', to: 'uploaded', actor: 'employee',
    guard: 'trip belongs to caller; trip not archived; at least one document stored',
    effects: [
      'insert receipt (entity_id from trip, user_id from trip)',
      'insert document rows (kind=photo, page_no) or (kind=xml)',
      'insert event upload.received',
      'if content is CFDI XML → enqueue ingest-cfdi{source:upload} (async) and SKIP extraction',
      'else enqueue extract-receipt{generation:1} (async)',
    ],
  },
  {
    from: 'uploaded', event: 'extract.claimed', to: 'extracting', actor: 'extract',
    guard: 'message.generation == receipt.current_generation',
    effects: ['insert event extract.claimed'],
  },
  {
    from: 'extracting', event: 'extract.completed', to: 'unconfirmed', actor: 'extract',
    guard: 'message.generation == receipt.current_generation; classification <> never_facturable OR split_kind in (coworkers, others) — a shared bill always gets the employee\'s check, so its share is confirmed (A4); all required confidences >= threshold (the voucher\'s amount and date too on a card share)',
    reason: 'from-classification',
    effects: [
      'write extraction_json, confidence_json, merchant, purchase_date, ocr_total, currency, folio, ticket_codes, facturacion_json, classification',
      'shared bill: ticket_total = the ticket\'s total; ocr_total = the share (voucher amount − tip, or the typed cash share); payment_amount = the voucher amount; voucher_json with the checks (share < ticket total, same date ±1 day, same merchant); voucher_hash',
      'shared bill: a voucher hash seen on another receipt → insert queue_item(owner=operator, reason=duplicate-voucher) — flag, never block',
      'compute image_hash; if a cross-user/cross-time match exists → insert queue_item(owner=operator, reason=duplicate-flag) — flag, never block',
      'insert event extract.completed',
    ],
  },
  {
    from: 'extracting', event: 'extract.completed', to: 'non-deductible', actor: 'extract',
    guard: 'classification == never_facturable (tip, informal vendor) AND split_kind not in (coworkers, others)',
    reason: 'never-facturable',
    effects: ['write extraction fields', 'insert event extract.completed'],
  },
  {
    from: 'extracting', event: 'extract.low_confidence', to: 'needs-retake', actor: 'extract',
    guard: 'any required field confidence < threshold',
    reason: 'extraction-failed',
    effects: [
      'write whatever was extracted (partial is fine)',
      'insert queue_item(owner=employee, user_id=receipt.user_id, reason=extraction-failed)',
      'insert event extract.low_confidence',
      'notify employee (async)',
    ],
  },
  {
    from: 'extracting', event: 'extract.failed', to: 'needs-retake', actor: 'extract',
    guard: 'poison / model error after retries',
    reason: 'extraction-failed',
    effects: [
      'insert queue_item(owner=employee, reason=extraction-failed)',
      'insert event extract.failed{error}',
      'notify employee (async)',
    ],
  },
  {
    from: ['unconfirmed', 'needs-retake'], event: 'voucher.received', to: 'uploaded', actor: 'employee',
    guard: 'caller owns receipt; split_kind in (coworkers, others) after the request (it may set it, from Por revisar); a voucher image or PDF',
    reason: null,
    effects: [
      'replace the receipt\'s voucher document (kind=voucher); the ticket\'s photos stay',
      'increment receipt.current_generation',
      'resolve open employee queue_item for this receipt',
      'insert event voucher.received',
      'enqueue extract-receipt{generation:new} (async) — the ticket and the voucher are read again',
    ],
  },
  {
    from: 'needs-retake', event: 'retake.received', to: 'uploaded', actor: 'employee',
    guard: 'caller owns receipt',
    effects: [
      'insert document rows (replace or append per request)',
      'increment receipt.current_generation',
      'resolve open employee queue_item for this receipt',
      'insert event retake.received',
      'enqueue extract-receipt{generation:new} (async)',
    ],
  },

  // ── Confirm ───────────────────────────────────────────────────────────────
  {
    from: 'unconfirmed', event: 'confirm.submitted', to: 'routed', actor: 'employee',
    guard: 'caller owns receipt; classification in (ticket_with_instructions, platform_receipt, no_channel); NOT cashGate; with PARTIAL_FACTURAS_ENABLED a ticket or PDF receipt has split_kind answered; a shared bill: 0 < share <= ticket_total',
    effects: [
      'write confirmed fields: merchant, purchase_date, confirmed_total, currency, folio, category, payment_method, covers, facturacion_json, uso_cfdi_override',
      'shared bill: write split_kind, ticket_total; confirmed_total is the share; a voucher check that still fails (date, merchant) → insert queue_item(owner=operator, reason=voucher-mismatch)',
      'a receipt on the same ticket (entity, merchant, folio, date) answered whole while another is shared → insert queue_item(owner=operator, reason=shared-ticket-conflict) — the meal would be paid twice',
      'compute window_deadline_at (from facturacion.window_text via deadlines.ts; default = last day of purchase month) and fiscal_deadline_at (America/Mexico_City)',
      'insert event confirm.submitted',
      'enqueue route-recovery (async)',
      'start deadline-orchestrator with instanceId=receipt_id (singleton) (async)',
    ],
  },
  {
    from: 'unconfirmed', event: 'confirm.submitted', to: 'non-deductible', actor: 'employee',
    guard: 'cashGate := payment=cash AND (category=combustible OR confirmed_total > 2000); applies to every classification except foreign — a CFDI paid in cash over the threshold is non-deductible whether it was already issued or not. Checked FIRST, so the other confirm transitions are exclusive. Also a shared bill read as never_facturable (its share confirmed, A4) → reason never-facturable. On a shared bill confirmed_total is the share: the gate measures the payment (A10).',
    reason: 'from-classification',   // cash-27iii, or never-facturable for a shared never_facturable ticket
    effects: ['write confirmed fields', 'insert event confirm.submitted{gate:27iii}', 'notify employee of reason (async)'],
  },
  {
    from: 'unconfirmed', event: 'confirm.submitted', to: 'foreign-pending-review', actor: 'employee',
    guard: 'classification == foreign (the cash gate does not apply — different regime)',
    effects: [
      'write confirmed fields + foreign_vendor{legal_name,address,tax_id,place,recipient_rfc_on_doc,recipient_name_on_doc}',
      'compute fiscal_deadline_at only',
      'insert queue_item(owner=operator, reason=foreign-review)',
      'insert event confirm.submitted{foreign}',
    ],
  },
  {
    from: 'unconfirmed', event: 'confirm.submitted', to: 'routed', actor: 'employee',
    guard: 'classification == already_factura (a photographed PDF, not an XML); NOT cashGate',
    effects: [
      'write confirmed fields',
      'insert event confirm.submitted{already_factura}',
      'enqueue ingest-cfdi{source:upload, from:pdf} (async) — deterministic parse of the QR/UUID on the PDF; falls back to matching against the pool',
    ],
  },

  // ── Routing ───────────────────────────────────────────────────────────────
  {
    from: 'routed', event: 'route.dispatched', to: 'awaiting', actor: 'route',
    guard: 'facturacion.channel in (email, portal, qr, platform) OR vendor dispatch rule fires; platform → targetUrl from the static platform table; portal with the portal backend disabled or an unusable URL AND an email present → email; qr with a URL payload → portal',
    effects: [
      'attempt_n = 1 + MAX(attempt_n) for (receipt, channel, target_domain), computed INSIDE the same transaction as the insert; a redelivery hits uq_inflight (one in_flight per receipt) and resumes instead',
      'insert recovery_attempt(outcome=in_flight) — BEFORE any external call',
      'insert event route.dispatched{channel,target}',
      'enqueue email-request | portal-attempt | vendor-request with attempt_n IN THE MESSAGE (async)',
    ],
  },
  {
    from: 'routed', event: 'route.partial_unsupported', to: 'queued', actor: 'route',
    guard: 'split_kind in (coworkers, others) AND the portal host is known partial_support = no (portal_capability)',
    reason: 'partial-not-supported',
    effects: [
      'insert queue_item(owner=employee, reason=partial-not-supported) — coworkers: owner=operator, reason=partial-coworkers (request the whole ticket once, split it)',
      'insert event route.partial_unsupported',
      'notify employee (async) — others only',
    ],
  },
  {
    from: 'routed', event: 'route.no_channel', to: 'queued', actor: 'route',
    guard: 'facturacion.channel == none, OR qr with no decodable payload and no URL (→ channel-not-captured, owner employee)',
    reason: 'from-attempt', // no-channel-printed vs channel-not-captured decided by extraction confidence on the footer region
    effects: [
      'insert queue_item(owner = employee if channel-not-captured else operator)',
      'insert event route.no_channel',
    ],
  },

  // ── Recovery outcomes ─────────────────────────────────────────────────────
  {
    from: 'awaiting', event: 'attempt.succeeded', to: 'awaiting', actor: 'recovery',
    guard: 'attempt row exists and is in_flight',
    effects: [
      'update recovery_attempt(outcome=success, result_ref, finished_at, cost_usd)',
      'insert event attempt.succeeded',
      'enqueue ingest-cfdi{source:portal|vendor, attemptId} (async) — state stays awaiting until ingest validates',
    ],
  },
  {
    from: 'awaiting', event: 'attempt.failed_retryable', to: 'awaiting', actor: 'recovery',
    guard: 'attempt_n < cap AND domain breaker closed AND now < window_deadline_at',
    reason: 'from-attempt',
    effects: [
      'update recovery_attempt(outcome=failure, failure_reason)',
      'insert recovery_attempt(attempt_n+1, in_flight)',
      'insert event attempt.failed{reason}',
      'enqueue next attempt with backoff (async)',
    ],
  },
  {
    from: 'awaiting', event: 'attempt.failed_after_window', to: 'queued', actor: 'recovery',
    guard: 'a retryable failure but now >= window_deadline_at — no more portal retries; the orchestrator will escalate',
    reason: 'from-attempt',
    effects: ['update recovery_attempt(outcome=failure, failure_reason)', 'insert queue_item(owner=operator, priority=urgent)', 'insert event attempt.failed_after_window'],
  },
  {
    from: 'awaiting', event: 'attempt.cap_exhausted', to: 'awaiting', actor: 'recovery',
    guard: 'attempt_n == cap AND vendor backend enabled',
    effects: [
      'update recovery_attempt(outcome=failure)',
      'insert recovery_attempt(channel=vendor, attempt_n=1, in_flight)',
      'insert event attempt.cap_exhausted{→vendor}',
      'enqueue vendor-request (async)',
    ],
  },
  {
    from: 'awaiting', event: 'attempt.cap_exhausted', to: 'queued', actor: 'recovery',
    guard: 'attempt_n == cap AND vendor backend disabled',
    reason: 'from-attempt',
    effects: ['update recovery_attempt(outcome=failure)', 'insert queue_item(owner=operator, reason=last failure)', 'insert event attempt.cap_exhausted{→queue}'],
  },
  {
    from: 'awaiting', event: 'attempt.breaker_open', to: 'queued', actor: 'recovery',
    guard: 'domain_circuit.breaker_open_until > now',
    reason: 'bot-check',
    effects: [
      'update recovery_attempt(outcome=failure, failure_reason=bot-check)',
      'insert queue_item(owner=operator, priority = urgent if window_deadline_at - now < 24h else normal)',
      'insert event attempt.breaker_open',
    ],
  },
  {
    from: 'awaiting', event: 'attempt.failed_terminal', to: 'queued', actor: 'recovery',
    guard: 'failure_reason in (datos-receptor-rechazados, bad-address, login-required, needs-input, partial-not-supported, phone-required, registration-failed, awaiting-confirmation, login-failed, confirmation-unverified, account-pending) — needs-input: an employee queue item carries the question; the answer re-routes (queue.input_supplied); awaiting-confirmation and account-pending carry no item: the account\'s confirmation or readiness re-routes (account.ready)',
    reason: 'from-attempt',
    effects: [
      'update recovery_attempt(outcome=failure, failure_reason)',
      'if datos-receptor-rechazados → insert queue_item(owner=operator, reason=refresh-csf) and NEVER retry the portal',
      'if partial-not-supported → portal_capability(host).partial_support = no; insert queue_item(owner=employee) — coworkers: owner=operator, reason=partial-coworkers; notify employee (async)',
      'else insert queue_item(owner=operator)',
      'insert event attempt.failed_terminal',
    ],
  },
  {
    from: 'awaiting', event: 'attempt.failed_terminal', to: 'queued', actor: 'recovery',
    guard: 'portal said "ya fue facturado" AND (an unresolved in_flight attempt exists OR pool has a same-emisor factura in window)',
    reason: 'already-global',
    effects: [
      'update recovery_attempt(outcome=failure, failure_reason=already-global)',
      'insert queue_item(owner=operator, reason=already-global, body=candidates) — NEVER terminal while an attempt may have succeeded',
      'insert event attempt.failed_terminal{already-global}',
    ],
  },

  // ── Ingest / validation ───────────────────────────────────────────────────
  {
    from: ['uploaded', 'routed', 'awaiting', 'queued', 'window-expired-escalated', 'refacturacion'], event: 'factura.validated', to: 'validated', actor: 'ingest',
    guard: 'factura.uuid unique or already-known; receptor rfc+nombre+cp match entity; total within category tolerance; date within window; candidate unique across ALL receipts including filed ones. From uploaded: the XML the employee uploaded (hintReceiptId), no matching needed.',
    reason: null,
    effects: [
      'if receipt.reported_in_period_id IS NOT NULL → insert adjustment(kind=late_factura, origin=reported period, signed_amount=+allocated) — the receipt was already exported as no-comprobado',
      'upsert factura (UUID unique; violation = already known → link to existing)',
      'insert receipt_factura(allocated_amount, match_method, is_primary=true if first)',
      'store xml/pdf documents',
      'resolve any open queue_item for this receipt',
      'signal deadline-orchestrator instance=receipt_id: completed (async)',
      'insert event factura.validated{uuid}',
      'enqueue sat-status-check{facturaId} (async)',
    ],
  },
  {
    from: ['uploaded', 'routed', 'awaiting', 'queued', 'window-expired-escalated'], event: 'factura.wrong_data', to: 'refacturacion', actor: 'ingest',
    guard: 'a factura matched this receipt but receptor rfc/nombre/cp or total failed validation',
    reason: 'from-attempt',
    effects: [
      'upsert factura; insert receipt_factura(is_primary=false)',
      'if receptor_rfc == employee personal RFC pattern → reason=own-rfc, insert queue_item(owner=employee, reason=own-rfc) + notify (async)',
      'else insert queue_item(owner=operator, reason=refacturacion, due_at=fiscal_deadline_at)',
      'insert event factura.wrong_data',
    ],
  },
  {
    from: ['uploaded', 'routed', 'awaiting', 'queued', 'window-expired-escalated'], event: 'factura.held', to: 'queued', actor: 'ingest',
    guard: 'the CFDI is above a shared bill\'s payment_amount and, for the same emisor and dates, looks like the whole ticket (>= ticket_total - the tip band, <= ticket_total x 1.3); or the receipt is the XML the employee uploaded for it',
    reason: 'partial-whole-cfdi',
    effects: [
      'NO receipt_factura link — the factura stays in the pool, the export never pays it twice',
      'insert queue_item(owner=operator, reason=partial-whole-cfdi, body={facturaId, uuid, total, the shared receipt, payment_amount, ticket_total})',
      'insert event factura.held',
    ],
  },
  {
    from: ['routed', 'awaiting', 'window-expired-escalated'], event: 'factura.ambiguous', to: 'queued', actor: 'ingest',
    guard: '>1 receipt candidate for a factura, or >1 factura candidate for a receipt. Only candidates in a from-state transition; filed/non-deductible candidates are named in the queue item body but not moved.',
    reason: 'ambiguous-match',
    effects: ['leave factura in unmatched pool', 'insert queue_item(owner=operator, reason=ambiguous-match, body=candidate ids)', 'insert event factura.ambiguous'],
  },
  {
    from: 'validated', event: 'filing.completed', to: 'filed', actor: 'ingest',
    guard: 'all document rows have drive_id+item_id+etag+content_hash',
    effects: ['insert event filing.completed'],
  },

  // ── Deadlines ─────────────────────────────────────────────────────────────
  {
    from: ['awaiting', 'queued'], event: 'window.expired', to: 'window-expired-escalated', actor: 'orchestrator',
    guard: 'now >= window_deadline_at AND no in_flight attempt',
    reason: 'window-expired',
    effects: [
      'insert event window.expired',
      'enqueue escalate-expired{receiptId} (async)',
      'insert queue_item(owner=operator, reason=window-expired, priority=normal)',
    ],
  },
  {
    from: 'window-expired-escalated', event: 'escalation.resolved_manual', to: 'queued', actor: 'escalation',
    guard: 'direct-request sent; vendor refused or silent after N days',
    effects: ['write denuncia tracking on queue_item.body', 'insert event escalation.resolved_manual'],
  },

  // ── Queue resolutions (operator) ──────────────────────────────────────────
  {
    from: 'queued', event: 'queue.retry', to: 'routed', actor: 'operator',
    guard: 'now < fiscal_deadline_at',
    reason: null,
    effects: ['resolve queue_item', 'insert event queue.retry', 'enqueue route-recovery (async) — attempt_n continues from last'],
  },
  {
    from: 'queued', event: 'queue.channel_supplied', to: 'routed', actor: ['employee', 'operator'],
    guard: 'a facturación email/url was supplied via attention.resolve or the operator console',
    reason: null,
    effects: ['write facturacion_json', 'resolve queue_item', 'insert event queue.channel_supplied', 'enqueue route-recovery (async)'],
  },
  {
    from: 'queued', event: 'queue.input_supplied', to: 'routed', actor: ['employee', 'operator'],
    guard: 'reason_code == needs-input; the answer is one of the question\'s options (or free text when it has none)',
    reason: null,
    effects: ['merge the answer into receipt.portal_hints_json', 'remember it in portal_hint when it depends on a ticket code', 'resolve the needs-input items', 'insert event queue.input_supplied', 'enqueue route-recovery (async)'],
  },
  {
    from: 'queued', event: 'account.ready', to: 'routed', actor: ['ingest', 'recovery', 'operator'],
    guard: 'reason_code in (awaiting-confirmation, account-pending, phone-required, registration-failed, login-failed, confirmation-unverified); the (host, company) account is active (a run, or the operator registered it by hand), or its confirmation link arrived and passed the checks (B6–B7)',
    reason: null,
    effects: ['insert event account.ready', 'enqueue route-recovery (async) — dispatched with the account (login, or confirm-login with the link\'s ref)'],
  },
  {
    from: 'queued', event: 'queue.self_request', to: 'awaiting', actor: ['employee', 'operator'],
    guard: 'reason_code == partial-not-supported; split_kind in (coworkers, others)',
    reason: 'self-request',
    effects: ['resolve the partial-not-supported items', 'insert event queue.self_request', 'the CFDI comes by mail and matches by fields against the share (the deadline orchestrator keeps the window)'],
  },
  {
    from: 'queued', event: 'queue.employee_give_up', to: 'non-deductible', actor: 'employee',
    guard: 'reason_code == partial-not-supported; split_kind in (coworkers, others); share_source == voucher (a typed cash share needs the operator: an operator item partial-give-up, resolved by queue.give_up)',
    reason: 'partial-not-supported',
    effects: ['resolve the partial-not-supported items', 'insert event queue.employee_give_up'],
  },
  {
    from: ['queued', 'awaiting', 'refacturacion', 'window-expired-escalated'], event: 'queue.manual_match', to: 'validated', actor: 'operator',
    guard: 'operator selected a pool factura and an allocation; SUM(allocations) <= factura.total',
    reason: null,
    effects: [
      'if reported_in_period_id IS NOT NULL → insert adjustment(kind=late_factura, +allocated)',
      'insert receipt_factura(match_method=manual_split, allocated_amount)',
      'resolve queue_item',
      'signal orchestrator completed (async)',
      'insert event queue.manual_match',
    ],
  },
  {
    from: ['queued', 'window-expired-escalated'], event: 'queue.give_up', to: 'non-deductible', actor: 'operator',
    guard: 'operator confirmed no factura is obtainable',
    reason: 'from-attempt',
    effects: ['resolve queue_item(resolution)', 'insert event queue.give_up{reason}', 'notify employee of reason (async)'],
  },

  // ── Foreign ───────────────────────────────────────────────────────────────
  {
    from: 'foreign-pending-review', event: 'foreign.complete', to: 'foreign-filed', actor: 'operator',
    guard: 'foreign_vendor has legal_name, address, tax_id, place; recipient_rfc_on_doc AND recipient_name_on_doc are true',
    effects: ['resolve queue_item', 'file documents', 'insert event foreign.complete'],
  },
  {
    from: 'foreign-pending-review', event: 'foreign.incomplete', to: 'queued', actor: 'operator',
    reason: 'foreign-incomplete',
    effects: ['insert queue_item(owner=employee, reason=foreign-incomplete, body=missing fields)', 'notify employee (async)', 'insert event foreign.incomplete'],
  },
  {
    from: 'queued', event: 'foreign.complete', to: 'foreign-filed', actor: 'operator',
    guard: 'reason_code == foreign-incomplete and the employee supplied the missing fields',
    effects: ['resolve queue_item', 'file documents', 'insert event foreign.complete'],
  },

  // ── Refacturación ─────────────────────────────────────────────────────────
  {
    from: 'refacturacion', event: 'refacturacion.completed', to: 'validated', actor: 'ingest',
    guard: 'a new factura validated for this receipt; old one marked superseded',
    reason: null,
    effects: ['set new factura.supersedes_factura_id = old', 'DEMOTE old receipt_factura.is_primary=0 first (uq_rf_primary), then insert the new link is_primary=1', 'if reported → insert adjustment(kind=late_factura, +allocated)', 'resolve queue_item', 'insert event refacturacion.completed'],
  },
  {
    from: 'refacturacion', event: 'refacturacion.abandoned', to: 'non-deductible', actor: ['operator', 'sweep'],
    guard: 'now >= fiscal_deadline_at OR operator gave up',
    reason: 'unrecovered-at-return',
    effects: ['resolve queue_item', 'insert event refacturacion.abandoned'],
  },

  // ── Sweeps ────────────────────────────────────────────────────────────────
  {
    from: ['awaiting', 'queued', 'window-expired-escalated', 'routed'], event: 'sweep.unrecovered', to: 'non-deductible', actor: 'sweep',
    guard: 'annual sweep; now >= fiscal_deadline_at; NO in_flight attempt for this receipt',
    reason: 'unrecovered-at-return',
    effects: ['resolve open queue_items', 'insert event sweep.unrecovered'],
  },
  {
    from: 'non-deductible', event: 'late_factura', to: 'validated', actor: 'ingest',
    guard: 'reason_code in (window-expired, unrecovered-at-return, already-global); fiscal_year.return_filed_at IS NULL for the ejercicio; factura validates',
    reason: null,
    effects: [
      'link factura',
      'if receipt.reported_in_period_id IS NOT NULL → insert adjustment(kind=late_factura, origin=reported period, signed_amount=+allocated)',
      'insert event late_factura',
    ],
  },
  {
    from: ['validated', 'filed'], event: 'factura.cancelled', to: 'refacturacion', actor: 'sat',
    guard: 'sat_status changed to cancelado',
    reason: 'cancelled-by-emisor',
    effects: [
      'update factura.sat_status',
      'set receipt_factura.is_primary=0 for the cancelled link so the matcher will accept a replacement',
      'if reported → insert adjustment(kind=cancellation, signed_amount=-allocated)',
      'insert queue_item(owner=operator, reason=cancelled-by-emisor, due_at=fiscal_deadline_at)',
      'insert event factura.cancelled',
    ],
  },

  // ── Operator overrides ────────────────────────────────────────────────────
  {
    from: ['uploaded', 'extracting', 'unconfirmed', 'needs-retake', 'routed', 'awaiting', 'queued',
           'window-expired-escalated', 'validated', 'refacturacion', 'foreign-pending-review'],
    event: 'operator.void', to: 'voided', actor: 'operator',
    guard: 'reason supplied',
    reason: 'operator-void',
    effects: ['if reported_in_period_id IS NOT NULL → insert adjustment(kind=void, signed_amount=-exported amount)', 'resolve open queue_items', 'terminate orchestrator instance (async)', 'insert event operator.void{reason}'],
  },
];

/** What each operator reason means, in the console's and the operator mails' words (queue_item.reason_code is free text; an unknown code shows as it is). */
export const OPERATOR_REASON_TEXT: Record<string, string> = {
  'human-reply': 'Un comercio respondió por correo', 'window-expiring': 'Se vence el plazo del comercio', 'window-expired': 'Se venció el plazo del comercio',
  'already-global': 'El portal dice que ya está en la factura global', 'captcha': 'El portal pidió un captcha', 'captcha-token-rejected': 'El portal rechazó el captcha',
  'login-required': 'El portal pide iniciar sesión', 'bot-check': 'El portal detectó automatización', 'portal-down': 'El portal no responde',
  'field-mismatch': 'El portal no aceptó los datos del ticket', 'datos-receptor-rechazados': 'El portal rechazó los datos fiscales', 'agent-low-confidence': 'El agente no supo qué pasó en el portal',
  'bad-address': 'El correo del comercio rebotó', 'ambiguous-match': 'Llegaron varias facturas posibles', 'filing-incomplete': 'Falta archivar documentos',
  'cfdi-invalid': 'Llegó una factura inválida', 'xml-missing': 'Falta el XML de una factura fotografiada', 'duplicate-flag': 'Posible comprobante duplicado',
  'foreign-review': 'Comprobante extranjero por revisar', 'refresh-csf': 'Hay que renovar la constancia fiscal', 'cancelled-by-emisor': 'El comercio canceló la factura',
  'efos-definitivo': 'El emisor está en la lista 69-B', 'no-channel-printed': 'El ticket no trae datos de facturación', 'tier2-halted': 'El portal no se intentó (nivel 2 apagado)',
  'step-cap': 'El portal agotó los pasos', 'token-cap': 'El portal agotó el presupuesto', 'timeout': 'El portal se quedó sin tiempo',
  'unrecovered-at-return': 'Cerró el ejercicio sin factura', 'foreign-incomplete': 'Al comprobante extranjero le faltan datos', 'channel-not-captured': 'No se capturaron los datos de facturación',
  'cfdi-unverified': 'El SAT no reconoce la factura', 'new-portal': 'Portal nuevo, ¿confirmar?', 'unsolicited-mail': 'Correo sin comprobante ni solicitud conocida', 'stale-attempt': 'Intento sin respuesta',
  'needs-input': 'El empleado no ha respondido la pregunta del portal',
  'partial-coworkers': 'Cuenta compartida entre compañeros: el portal solo factura el ticket completo', 'partial-whole-cfdi': 'Llegó la factura del ticket completo de una cuenta compartida',
  'voucher-mismatch': 'El voucher no coincide con el ticket', 'duplicate-voucher': 'El mismo voucher en dos comprobantes', 'shared-ticket-conflict': 'El mismo ticket, completo y compartido',
  'partial-give-up': 'Parte en efectivo sin comprobante: el empleado pide marcarla sin factura', 'partial-not-supported': 'El portal solo factura el ticket completo',
  'account-manual': 'Registra la cuenta de la empresa en el portal a mano', 'account-mail': 'Correo de un portal sobre la cuenta de la empresa',
  'phone-required': 'El portal pide teléfono para registrar la cuenta', 'registration-failed': 'El portal no aceptó el registro de la cuenta',
  'login-failed': 'El portal rechazó la cuenta de la empresa', 'confirmation-unverified': 'La confirmación de la cuenta no se pudo comprobar',
};

/** Reason codes the employee sees, with the Spanish shown on the receipt detail screen. */
export const EMPLOYEE_VISIBLE_REASONS: Record<string, string> = {
  'never-facturable':      'Este comprobante no se puede facturar (propina o comercio sin facturación).',
  'cash-27iii':            'Pagado en efectivo por más de $2,000 o gasolina en efectivo: no es deducible.',
  'already-global':        'El comercio ya lo incluyó en su factura global al público en general.',
  'window-expired':        'Se venció el plazo del comercio. Seguimos intentando por otra vía.',
  'unrecovered-at-return': 'No se consiguió la factura antes de la declaración anual.',
  'own-rfc':               'La factura salió a tu RFC. Pídela a nombre de la empresa la próxima vez.',
  'extraction-failed':     'No pudimos leer la foto. Vuelve a tomarla.',
  'channel-not-captured':  'No alcanzamos a ver los datos de facturación. Fotografía el pie del ticket.',
  'foreign-incomplete':    'Al comprobante extranjero le faltan datos del proveedor.',
  'needs-input':           'El portal del comercio pide un dato que solo tú sabes. Contéstalo y seguimos.',
  'partial-not-supported': 'El portal del comercio solo factura el ticket completo, no tu parte. Elige cómo seguimos.',
  'self-request':          'Pediste tú la factura de tu parte al comercio. La esperamos por correo.',
};

/**
 * State → employee label → trip bucket → counts in total. Mirrors backend-spec §6b.
 * OVERRIDE: any receipt with an OPEN queue_item(owner='employee') displays 'Necesita tu atención'
 * regardless of its state label (e.g. queued/channel-not-captured, refacturacion/own-rfc).
 */
export const EMPLOYEE_VIEW: Record<ReceiptState, { label: string; bucket: 'pendientes' | 'con_factura' | 'sin_factura' | 'hidden'; countsInTotal: boolean }> = {
  'uploaded':                 { label: 'Leyendo…',              bucket: 'pendientes',  countsInTotal: true },
  'extracting':               { label: 'Leyendo…',              bucket: 'pendientes',  countsInTotal: true },
  'unconfirmed':              { label: 'Por revisar',           bucket: 'pendientes',  countsInTotal: true },
  'needs-retake':             { label: 'Necesita tu atención',  bucket: 'pendientes',  countsInTotal: true },
  'routed':                   { label: 'Solicitada',            bucket: 'pendientes',  countsInTotal: true },
  'awaiting':                 { label: 'Solicitada',            bucket: 'pendientes',  countsInTotal: true },
  'queued':                   { label: 'Solicitada',            bucket: 'pendientes',  countsInTotal: true },
  'window-expired-escalated': { label: 'Solicitada',            bucket: 'pendientes',  countsInTotal: true },
  'refacturacion':            { label: 'Solicitada',            bucket: 'pendientes',  countsInTotal: true },
  'validated':                { label: 'Con factura',           bucket: 'con_factura', countsInTotal: true },
  'filed':                    { label: 'Con factura',           bucket: 'con_factura', countsInTotal: true },
  'foreign-pending-review':   { label: 'Necesita tu atención',  bucket: 'pendientes',  countsInTotal: true },
  'foreign-filed':            { label: 'Comprobante extranjero', bucket: 'con_factura', countsInTotal: true },
  'non-deductible':           { label: 'Sin factura',           bucket: 'sin_factura', countsInTotal: true },
  'voided':                   { label: '',                      bucket: 'hidden',      countsInTotal: false },
};

/**
 * The one function that changes receipt.state. Sketch of the contract; the
 * implementation lives in the data layer and is the only caller of UPDATE receipt SET state.
 *
 *   const t = findTransition(from, event, actor, ctx);   // throws IllegalTransition if none
 *   assert(t.guard satisfied by ctx)                     // throws GuardFailed
 *   UPDATE receipt SET state=t.to, reason_code=…, row_version=… WHERE receipt_id=@id AND state=@from AND row_version=@rv
 *   if rowcount == 0 → throw Stale (caller re-reads and decides; never retries blindly)
 *   run same-transaction effects; commit; enqueue async effects
 */
export declare function transition(args: {
  receiptId: string;
  expectedState: ReceiptState;
  expectedRowVersion: Uint8Array;   // the ROWVERSION bytes from the read
  event: Event;
  actor: Actor;
  ctx: Record<string, unknown>;
}): Promise<{ to: ReceiptState }>;
